import React, { useState } from 'react';
import axios from 'axios';
import { MapPin, Phone, Mail, Send, ArrowUpRight, Loader2 } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { CONTACT } from '../mock';
import useReveal from '../hooks/useReveal';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from '../hooks/use-toast';
import { Toaster } from './ui/toaster';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLang();
  const [ref, visible] = useReveal();
  const [form, setForm] = useState({ name: '', email: '', dates: '', guests: '', message: '' });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/booking`, form);
      toast({ title: t('contact.success') });
      setForm({ name: '', email: '', dates: '', guests: '', message: '' });
    } catch (err) {
      toast({ title: t('contact.error'), variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const info = [
    { icon: MapPin, label: t('contact.addressLabel'), value: CONTACT.address, href: CONTACT.mapsUrl },
    { icon: Phone, label: t('contact.phoneLabel'), value: CONTACT.phone, href: `tel:${CONTACT.phoneHref}` },
    { icon: Mail, label: t('contact.emailLabel'), value: CONTACT.email, href: `mailto:${CONTACT.email}` },
  ];

  const inputCls = 'bg-[#F6EFE3] border-[#E7DAC4] focus-visible:ring-[#B5603A] text-[#2A2420] placeholder:text-[#a99e8f] font-body h-12 rounded-[2px]';

  return (
    <section id="contatti" className="py-24 md:py-36 bg-[#EFE5D4]">
      <div ref={ref} className={`max-w-7xl mx-auto px-5 md:px-10 reveal ${visible ? 'is-visible' : ''}`}>
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="font-body text-[#B5603A] tracking-[0.35em] uppercase text-xs mb-5">{t('contact.kicker')}</p>
          <h2 className="font-display text-4xl md:text-6xl text-[#2A2420] mb-5">{t('contact.title')}</h2>
          <p className="font-body text-[#6E655B] text-lg leading-relaxed">{t('contact.subtitle')}</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-start">
          {/* Info */}
          <div className="lg:col-span-2 space-y-4">
            {info.map((item, i) => (
              <a
                key={i}
                href={item.href}
                target={item.icon === MapPin ? '_blank' : undefined}
                rel="noopener noreferrer"
                className="group flex items-start gap-4 bg-[#F6EFE3] p-6 rounded-[2px] hover:shadow-[0_15px_35px_-20px_rgba(42,36,32,0.5)] transition-all duration-300"
              >
                <span className="shrink-0 w-11 h-11 rounded-full bg-[#B5603A]/10 flex items-center justify-center group-hover:bg-[#B5603A] transition-colors">
                  <item.icon className="w-5 h-5 text-[#B5603A] group-hover:text-white transition-colors" />
                </span>
                <div>
                  <p className="font-body text-xs tracking-[0.2em] uppercase text-[#B5603A] mb-1">{item.label}</p>
                  <p className="font-body text-[#2A2420] leading-snug">{item.value}</p>
                </div>
              </a>
            ))}
            <a
              href={CONTACT.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 font-body text-sm text-[#B5603A] hover:text-[#8F4A2C] transition-colors mt-2 ml-1"
            >
              {t('contact.directions')} <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>

          {/* Form */}
          <form onSubmit={onSubmit} className="lg:col-span-3 bg-[#F6EFE3] p-7 md:p-10 rounded-[2px] shadow-[0_25px_55px_-30px_rgba(42,36,32,0.5)]">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="font-body text-sm text-[#6E655B] mb-2 block">{t('contact.name')}</label>
                <Input name="name" value={form.name} onChange={onChange} required className={inputCls} />
              </div>
              <div>
                <label className="font-body text-sm text-[#6E655B] mb-2 block">{t('contact.email')}</label>
                <Input type="email" name="email" value={form.email} onChange={onChange} required className={inputCls} />
              </div>
              <div>
                <label className="font-body text-sm text-[#6E655B] mb-2 block">{t('contact.dates')}</label>
                <Input name="dates" value={form.dates} onChange={onChange} placeholder="01/08 - 05/08" className={inputCls} />
              </div>
              <div>
                <label className="font-body text-sm text-[#6E655B] mb-2 block">{t('contact.guests')}</label>
                <Input type="number" min="1" name="guests" value={form.guests} onChange={onChange} placeholder="2" className={inputCls} />
              </div>
            </div>
            <div className="mt-5">
              <label className="font-body text-sm text-[#6E655B] mb-2 block">{t('contact.message')}</label>
              <Textarea
                name="message"
                value={form.message}
                onChange={onChange}
                placeholder={t('contact.placeholderMsg')}
                rows={4}
                className="bg-[#F6EFE3] border-[#E7DAC4] focus-visible:ring-[#B5603A] text-[#2A2420] placeholder:text-[#a99e8f] font-body rounded-[2px]"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              className="mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-2 font-body tracking-wide px-9 py-3.5 rounded-full bg-[#B5603A] text-white hover:bg-[#8F4A2C] transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> {t('contact.sending')}</>
              ) : (
                <><Send className="w-4 h-4" /> {t('contact.send')}</>
              )}
            </button>
          </form>
        </div>
      </div>
      <Toaster />
    </section>
  );
}
